import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { currentSubscription, disablePush, enablePush, isIOS, isStandalone, pushSupported } from "@/lib/push";

const Box = ({ children, tone = "slate" }) => (
  <div className={`rounded-xl border p-5 ${tone === "on" ? "border-emerald-200 bg-emerald-50" : "border-indigo-200 bg-indigo-50"}`} data-testid="notifications-card">{children}</div>
);

export const NotificationsCard = () => {
  const [state, setState] = useState("loading");
  const check = async () => {
    if (isIOS() && !isStandalone()) return setState("ios_install");
    if (!pushSupported()) return setState("unsupported");
    if (Notification.permission === "denied") return setState("denied");
    const sub = await currentSubscription();
    if (!sub) return setState("off");
    const { data } = await api.get("/me/push/status", { params: { endpoint: sub.endpoint } });
    setState(data.subscribed ? "on" : "off");
  };
  useEffect(() => {
    check().catch(() => setState("off"));
  }, []);

  const run = (fn, msg) => async () => {
    try {
      await fn();
      if (msg) toast.success(msg);
      await check();
    } catch (e) {
      toast.error(e.message && !e.response ? e.message : errMsg(e));
    }
  };

  if (state === "loading") return null;
  if (state === "on") return (
    <Box tone="on">
      <p className="flex items-center gap-2 font-semibold text-emerald-800" data-testid="notifications-status"><Bell className="h-4 w-4" />Notifications are on for this device</p>
      <p className="mt-1 text-sm text-emerald-900/80">You'll get alerts when the rota is published, requests are decided and extra shifts are offered.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full bg-white" onClick={run(() => api.post("/me/push/test"), "Test notification sent")} data-testid="notifications-test-btn">Send a test</Button>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={run(disablePush, "Notifications turned off")} data-testid="notifications-off-btn"><BellOff className="mr-1 h-4 w-4" />Turn off</Button>
      </div>
    </Box>
  );
  const copy = {
    ios_install: ["Get shift alerts on your iPhone", <>Tap <Share className="inline h-4 w-4" /> <b>Share</b>, then <b>Add to Home Screen</b>. Open StaffRotas from your Home Screen and turn on notifications here.</>],
    unsupported: ["Notifications aren't available here", "This browser can't show notifications. You'll still get emails. On a phone, open this page in Chrome or Safari."],
    denied: ["Notifications are blocked", "Allow notifications for this site in your phone or browser settings, then come back here."],
    off: ["Turn on shift alerts", "Get a notification when the rota is published, a request is decided or an extra shift is offered to you."],
  }[state];
  return (
    <Box>
      <p className="flex items-center gap-2 font-semibold text-indigo-900" data-testid="notifications-status"><Bell className="h-4 w-4" />{copy[0]}</p>
      <p className="mt-1 text-sm text-indigo-900/80">{copy[1]}</p>
      {state === "off" && <Button size="sm" className="mt-3 rounded-full" onClick={run(enablePush, "Notifications are on")} data-testid="notifications-enable-btn">Turn on notifications</Button>}
    </Box>
  );
};
